import { useDemoStore, type Role } from "@/store/useDemoStore";

export type IaContext = ReturnType<typeof useDemoStore.getState>;

export interface IaRule {
  id: string;
  keywords: string[];
  build: (ctx: IaContext) => string;
}

export interface IaRoleConfig {
  agentName: string;
  description: string;
  greeting: (ctx: IaContext) => string;
  quickChips: string[];
  rules: IaRule[];
  fallback: (ctx: IaContext, userText: string) => string;
}

function effectiveUserName(ctx: IaContext): string {
  const id = ctx.settings.effectiveUserId;
  if (id && ctx.users[id]) return ctx.users[id].nombre.split(" ")[0];
  return "colega";
}

function pickPrimaryCandidate(ctx: IaContext) {
  const id = ctx.settings.effectiveUserId;
  if (id) {
    const u = ctx.users[id];
    if (u?.roles.includes("alumno") && ctx.candidates[u.id]) return ctx.candidates[u.id];
  }
  return Object.values(ctx.candidates)[0];
}

function expedientesOfCandidate(ctx: IaContext, candidatoId: string) {
  return Object.values(ctx.expedientes).filter((e) => e.candidatoId === candidatoId);
}

function pct(n: number, d: number): number {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}

// --- ADMIN ----------------------------------------------------------------
const adminConfig: IaRoleConfig = {
  agentName: "CertiAI · Copiloto institucional",
  description:
    "Observatorio interno: te da el pulso de estándares, grupos, dictámenes y certificaciones de la institución en tiempo real, con base en el estado actual del sistema.",
  greeting: (ctx) =>
    `Hola ${effectiveUserName(ctx)}, soy CertiAI. Puedo darte un panorama operativo de CertiCore con los datos que hay cargados ahora mismo. ¿Por dónde empezamos?`,
  quickChips: [
    "¿Cuántos certificados se emitieron?",
    "¿Qué estándares están publicados?",
    "Estado de grupos en curso",
    "Resumen de alertas",
  ],
  rules: [
    {
      id: "certificados",
      keywords: ["certificad", "emitid", "folio"],
      build: (ctx) => {
        const cert = Object.values(ctx.expedientes).filter((e) => e.certificado.emitido);
        const ult = [...Object.values(ctx.dictamenes)]
          .filter((d) => d.resultado === "competente" && !d.revocacion)
          .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
          .slice(0, 3)
          .map((d) => `· ${d.certificadoId ?? d.id} (${d.fecha.slice(0, 10)})`)
          .join("\n");
        return `Hay **${cert.length} certificados emitidos** activos en el sistema. Los más recientes:\n${ult || "· (aún ninguno)"}`;
      },
    },
    {
      id: "estandares",
      keywords: ["estándar", "estandar", "publicad", "borrador"],
      build: (ctx) => {
        const all = Object.values(ctx.standards);
        const pub = all.filter((s) => s.estado === "publicado");
        const rev = all.filter((s) => s.estado === "en-revision");
        const bor = all.filter((s) => s.estado === "borrador");
        return `Tienes **${pub.length} estándar(es) publicado(s)**, ${rev.length} en revisión y ${bor.length} en borrador.\nPublicados: ${pub.map((s) => s.codigo).join(", ") || "ninguno"}.`;
      },
    },
    {
      id: "grupos",
      keywords: ["grupo", "generación", "generacion", "sede"],
      build: (ctx) => {
        const all = Object.values(ctx.groups);
        const enCurso = all.filter((g) => g.estado === "en-curso");
        const planificados = all.filter((g) => g.estado === "planeado");
        return `Operación de grupos: **${enCurso.length} en curso** y ${planificados.length} planeados, de ${all.length} totales.\n${enCurso
          .slice(0, 3)
          .map((g) => `· ${g.nombre} — ${g.sede ?? "sin sede"} (${g.inicio} → ${g.fin})`)
          .join("\n")}`;
      },
    },
    {
      id: "candidatos",
      keywords: ["candidato", "aspirante", "alumno"],
      build: (ctx) => {
        const cands = Object.values(ctx.candidates);
        const cert = cands.filter((c) => c.estado === "certificado").length;
        return `Padrón actual: **${cands.length} candidatos** registrados, ${cert} ya certificados. Avance promedio: ${pct(
          cands.reduce((a, c) => a + c.progreso, 0),
          cands.length * 100,
        )}%.`;
      },
    },
    {
      id: "alertas",
      keywords: ["alerta", "riesgo", "inconsist", "auditor"],
      build: (ctx) => {
        const pend = Object.values(ctx.expedientes).filter((e) => e.dictamen === "pendiente" && e.evaluacionPractica.aplicada).length;
        return `Resumen rápido para auditoría:\n· ${pend} expedientes esperando dictamen.\n· ${ctx.auditLog.length} eventos registrados en bitácora.\nLa vista completa vive en el panel del auditor.`;
      },
    },
    {
      id: "usuarios",
      keywords: ["usuario", "rol", "persona"],
      build: (ctx) => {
        const u = Object.values(ctx.users);
        const porRol: Record<string, number> = {};
        for (const x of u) for (const r of x.roles) porRol[r] = (porRol[r] ?? 0) + 1;
        return `Hay **${u.length} usuarios** dados de alta. Distribución por rol:\n${Object.entries(porRol)
          .map(([r, n]) => `· ${r}: ${n}`)
          .join("\n")}`;
      },
    },
  ],
  fallback: () =>
    "Puedo ayudarte con métricas de operación (certificados, estándares, grupos, candidatos, usuarios, alertas). Prueba con una de las sugerencias o pregunta usando esas palabras clave.",
};

// --- ALUMNO ---------------------------------------------------------------
const alumnoConfig: IaRoleConfig = {
  agentName: "CertiAI · Coach del candidato",
  description:
    "Te acompaña durante tu certificación: dudas sobre el estándar, qué evidencias subir, cómo es la práctica y qué te falta para el dictamen.",
  greeting: (ctx) => {
    const cand = pickPrimaryCandidate(ctx);
    if (!cand) return "Hola, soy CertiAI. Cuando tu administrador te inscriba en un estándar podré orientarte sobre tu ruta.";
    return `Hola ${cand.nombre.split(" ")[0]}, soy tu coach CertiAI. Llevas un avance del ${cand.progreso}% en tu ruta. ¿En qué te ayudo hoy?`;
  },
  quickChips: [
    "¿Cómo voy con mi ruta?",
    "¿Qué evidencias me faltan?",
    "¿Cómo es la práctica?",
    "¿Cuándo puedo presentar el dictamen?",
  ],
  rules: [
    {
      id: "avance",
      keywords: ["avance", "voy", "progreso", "ruta", "lms", "módulo", "modulo"],
      build: (ctx) => {
        const cand = pickPrimaryCandidate(ctx);
        if (!cand) return "Aún no estás inscrito en un estándar.";
        const exp = expedientesOfCandidate(ctx, cand.id)[0];
        if (!exp) return `${cand.nombre.split(" ")[0]}, todavía no tienes un expediente abierto.`;
        const route = ctx.lmsRouteByStandard[exp.estandar];
        const prog = ctx.lmsProgressByExpediente[exp.id];
        const completos = prog?.completedModuleIds.length ?? 0;
        const total = route?.modules.length ?? 0;
        const p = pct(completos, total);
        const sig = route?.modules.find((m) => !(prog?.completedModuleIds ?? []).includes(m.id));
        return `Vas en **${completos}/${total} módulos (${p}%)** de la ruta ${exp.estandar}. ${
          sig ? `Tu siguiente módulo recomendado es: *${sig.titulo}* (${sig.duracionMin} min).` : "¡Completaste todos los módulos!"
        }`;
      },
    },
    {
      id: "evidencias",
      keywords: ["evidencia", "documento", "subir", "expediente"],
      build: (ctx) => {
        const cand = pickPrimaryCandidate(ctx);
        if (!cand) return "Sin candidato activo no puedo revisar tus evidencias.";
        const exp = expedientesOfCandidate(ctx, cand.id)[0];
        if (!exp) return "Aún no tienes expediente abierto.";
        const docs = ctx.expedienteDocs[exp.id] ?? [];
        const faltantes = docs.filter((d) => d.estado === "faltante");
        const observadas = docs.filter((d) => d.estado === "observado");
        const pendientes = [...faltantes, ...observadas];
        return `Tu expediente **${exp.id.toUpperCase()}** tiene ${docs.length} evidencias. ${
          pendientes.length === 0
            ? "Todas están cargadas y sin observaciones, ¡bien hecho!"
            : `Faltan ${faltantes.length} por subir y ${observadas.length} con observaciones: ${pendientes.slice(0, 3).map((d) => `*${d.nombre}*`).join(", ")}.`
        }`;
      },
    },
    {
      id: "practica",
      keywords: ["práctica", "practica", "simulador", "preop", "checklist"],
      build: (ctx) => {
        const cand = pickPrimaryCandidate(ctx);
        if (!cand) return "Sin candidato activo no puedo darte detalle de la práctica.";
        const exp = expedientesOfCandidate(ctx, cand.id)[0];
        if (!exp) return "Aún no tienes expediente abierto.";
        return `La práctica se aplica en sitio con un evaluador certificado. Antes corre el **simulador preoperativo** desde tu ruta — te ayuda a no olvidar nada. Estado actual de tu práctica: ${
          exp.evaluacionPractica.aplicada
            ? `aplicada (${exp.evaluacionPractica.criteriosCompetente}/${exp.evaluacionPractica.criteriosTotales} criterios competentes).`
            : "aún no aplicada."
        }`;
      },
    },
    {
      id: "dictamen",
      keywords: ["dictamen", "competente", "certificado", "cuando"],
      build: (ctx) => {
        const cand = pickPrimaryCandidate(ctx);
        if (!cand) return "Sin candidato activo no puedo proyectar tu dictamen.";
        const exp = expedientesOfCandidate(ctx, cand.id)[0];
        if (!exp) return "Aún no tienes expediente.";
        if (exp.certificado.emitido)
          return `¡Ya estás certificado! Folio **${exp.certificado.folio}** emitido el ${exp.certificado.fechaEmision}.`;
        const faltan: string[] = [];
        if (!exp.diagnostico.aplicado) faltan.push("diagnóstico");
        if (!exp.examenTeorico.aplicado) faltan.push("examen teórico");
        if (!exp.evaluacionPractica.aplicada) faltan.push("evaluación práctica");
        return faltan.length === 0
          ? "Tu expediente está listo para el dictamen. El dictaminador lo revisará en breve."
          : `Para llegar al dictamen aún te falta completar: ${faltan.join(", ")}.`;
      },
    },
  ],
  fallback: () =>
    "Como soy una demo simulada, mejor te ayudo con: tu avance en la ruta, evidencias pendientes, cómo es la práctica o qué te falta para el dictamen.",
};

// --- INSTRUCTOR -----------------------------------------------------------
const instructorConfig: IaRoleConfig = {
  agentName: "CertiAI · Apoyo docente",
  description:
    "Te ayuda a leer el avance de tus candidatos, identificar a quiénes intervenir y preparar materiales o sesiones del grupo.",
  greeting: (ctx) =>
    `Hola ${effectiveUserName(ctx)}, soy tu apoyo CertiAI. Puedo darte el pulso de tu grupo y sugerirte intervenciones.`,
  quickChips: [
    "¿Quién va atrasado?",
    "Asistencia del grupo",
    "Próximas sesiones",
    "Sugiere material para mi grupo",
  ],
  rules: [
    {
      id: "atrasados",
      keywords: ["atras", "riesgo", "intervención", "intervencion", "bajo"],
      build: (ctx) => {
        const cands = Object.values(ctx.candidates);
        const bajos = cands.filter((c) => c.progreso < 50).sort((a, b) => a.progreso - b.progreso).slice(0, 4);
        if (bajos.length === 0) return "Buenas noticias: ningún candidato está por debajo del 50% de avance.";
        return `Candidatos con avance bajo a priorizar:\n${bajos
          .map((c) => `· ${c.nombre} — ${c.progreso}% (${c.unidad})`)
          .join("\n")}\nSugerencia: agenda una sesión breve de refuerzo y deja una observación en su expediente.`;
      },
    },
    {
      id: "asistencia",
      keywords: ["asistencia", "ausente", "asistir"],
      build: (ctx) => {
        const byGroup = ctx.attendanceByGroup;
        const grupos = Object.keys(byGroup);
        if (grupos.length === 0) return "Aún no se ha registrado asistencia.";
        let p = 0, a = 0, t = 0;
        for (const sesiones of Object.values(byGroup)) {
          for (const candMap of Object.values(sesiones)) {
            for (const m of Object.values(candMap)) {
              if (m.estado === "presente") p++;
              else if (m.estado === "tarde") t++;
              else if (m.estado === "ausente") a++;
            }
          }
        }
        const total = p + a + t;
        return `Asistencia consolidada: **${pct(p, total)}% presente**, ${pct(t, total)}% tarde, ${pct(a, total)}% ausente (${total} marcas registradas).`;
      },
    },
    {
      id: "sesiones",
      keywords: ["sesión", "sesion", "calendario", "próxima", "proxima"],
      build: (ctx) => {
        const today = new Date().toISOString().slice(0, 10);
        const next = Object.values(ctx.sessionsByGroup)
          .flat()
          .filter((s) => s.fecha >= today)
          .sort((a, b) => (a.fecha + a.hora).localeCompare(b.fecha + b.hora))
          .slice(0, 4);
        if (next.length === 0) return "No tienes sesiones futuras programadas.";
        return `Próximas sesiones:\n${next.map((s) => `· ${s.fecha} ${s.hora} — ${s.titulo} (${s.tipo})`).join("\n")}`;
      },
    },
    {
      id: "material",
      keywords: ["material", "didáctico", "didactico", "recurso"],
      build: (ctx) => {
        const all = Object.values(ctx.materialsByGroup).flat();
        return `Tienes **${all.length} recursos didácticos** cargados. Tipos: ${[...new Set(all.map((m) => m.tipo))].join(", ") || "—"}. Sugerencia: agrega un caso práctico cercano al sector del grupo para reforzar el módulo más bajo.`;
      },
    },
  ],
  fallback: () =>
    "Puedo apoyarte con: candidatos atrasados, asistencia, próximas sesiones o ideas de material didáctico.",
};

// --- EVALUADOR ------------------------------------------------------------
const evaluadorConfig: IaRoleConfig = {
  agentName: "CertiAI · Asistente del evaluador",
  description:
    "Te recuerda criterios críticos, sugiere el orden de la evaluación práctica y te ayuda a redactar observaciones consistentes.",
  greeting: (ctx) =>
    `Hola ${effectiveUserName(ctx)}, soy tu asistente CertiAI. Estoy aquí para reforzar tus criterios, no para sustituir tu juicio.`,
  quickChips: [
    "¿Qué criterios son críticos?",
    "Recordatorio de prechecklist",
    "Cómo redactar una observación",
    "Mis evaluaciones pendientes",
  ],
  rules: [
    {
      id: "criterios",
      keywords: ["criterio", "crítico", "critico", "bloqueante"],
      build: (ctx) => {
        const items = ctx.prechecklistTemplateByStandard["EC1781"] ?? [];
        const criticos = items.filter((i) => i.critical);
        if (criticos.length === 0) return "No hay criterios marcados como críticos en EC1781.";
        return `Criterios críticos del EC1781 (no deben omitirse):\n${criticos.map((c) => `· ${c.label}`).join("\n")}`;
      },
    },
    {
      id: "prechecklist",
      keywords: ["prechecklist", "preoperativ", "lista", "antes"],
      build: () =>
        "Antes de habilitar el circuito: recorre la lista preoperativa, registra observación en los puntos críticos faltantes y solo entonces autoriza el inicio. La práctica no se podrá enviar si quedan ítems críticos sin marcar como *cumple*.",
    },
    {
      id: "observacion",
      keywords: ["observ", "redact", "comentario", "nota"],
      build: () =>
        "Estructura sugerida para tus observaciones:\n· **Hecho observado** (qué hizo el candidato, en presente: 'aplica freno…')\n· **Criterio de referencia** (qué ítem evaluaba)\n· **Brecha** (qué faltó respecto al criterio)\n· **Recomendación** (acción concreta para reforzar).",
    },
    {
      id: "pendientes",
      keywords: ["pendiente", "asignad", "agenda", "bandeja"],
      build: (ctx) => {
        const pend = Object.values(ctx.expedientes).filter(
          (e) => e.dictamen === "pendiente" && !e.evaluacionPractica.aplicada,
        ).length;
        return `Tienes aproximadamente **${pend} expedientes** sin práctica aplicada en la operación actual. Atiende primero los que ya tengan prechecklist completa.`;
      },
    },
  ],
  fallback: () =>
    "Puedo recordarte criterios críticos, ayudarte con la lista preoperativa, sugerirte cómo redactar observaciones o resumir tu bandeja.",
};

// --- DICTAMINADOR ---------------------------------------------------------
const dictaminadorConfig: IaRoleConfig = {
  agentName: "CertiAI · Pre-dictamen",
  description:
    "Aplica las reglas vigentes a cada expediente y propone un pre-dictamen sobreescribible. Toda decisión queda en bitácora con tu fundamento.",
  greeting: (ctx) =>
    `Hola ${effectiveUserName(ctx)}, soy tu copiloto de dictamen. Mis recomendaciones son una guía: tu juicio profesional manda.`,
  quickChips: [
    "Expedientes pendientes",
    "¿Cómo se aplican las reglas?",
    "Resumen de competentes",
    "Última actividad",
  ],
  rules: [
    {
      id: "pendientes",
      keywords: ["pendiente", "bandeja", "cuántos", "cuantos"],
      build: (ctx) => {
        const pend = Object.values(ctx.expedientes).filter(
          (e) => e.dictamen === "pendiente" && e.evaluacionPractica.aplicada,
        );
        return `Hay **${pend.length} expedientes listos para dictamen** (con práctica ya aplicada).\n${pend
          .slice(0, 4)
          .map((e) => `· ${e.id.toUpperCase()} — ${e.estandar} (${e.evaluacionPractica.criteriosCompetente}/${e.evaluacionPractica.criteriosTotales})`)
          .join("\n")}`;
      },
    },
    {
      id: "reglas",
      keywords: ["regla", "fundamento", "bloque", "advertencia"],
      build: (ctx) => {
        const rules = ctx.rulesByStandard["EC1781"] ?? [];
        const bloq = rules.filter((r) => r.severidad === "bloqueante").length;
        const adv = rules.filter((r) => r.severidad === "advertencia").length;
        return `Para EC1781 están activas **${rules.length} reglas** (${bloq} bloqueantes, ${adv} advertencias). Las bloqueantes impiden 'competente' aunque la práctica esté completa; las advertencias pueden sobreescribirse con fundamento.`;
      },
    },
    {
      id: "competentes",
      keywords: ["competente", "emitid", "certificado", "estad"],
      build: (ctx) => {
        const total = Object.values(ctx.expedientes).length;
        const comp = Object.values(ctx.expedientes).filter((e) => e.dictamen === "competente").length;
        const nc = Object.values(ctx.expedientes).filter((e) => e.dictamen === "todavia-no-competente").length;
        return `Resultados del proceso actual: **${comp} competentes**, ${nc} todavía no competentes, ${total - comp - nc} pendientes. Tasa de competencia: ${pct(comp, comp + nc)}%.`;
      },
    },
    {
      id: "actividad",
      keywords: ["actividad", "última", "ultima", "reciente", "bitácora", "bitacora"],
      build: (ctx) => {
        const ult = ctx.auditLog.slice(0, 3);
        if (ult.length === 0) return "Aún no hay eventos registrados en bitácora.";
        return `Últimos eventos:\n${ult.map((e) => `· ${e.ts.slice(0, 16).replace("T", " ")} — ${e.actor} ${e.accion} (${e.entidad})`).join("\n")}`;
      },
    },
  ],
  fallback: () =>
    "Como copiloto de dictamen, puedo resumirte la bandeja, las reglas vigentes, la tasa de competencia o la actividad reciente.",
};

// --- AUDITOR --------------------------------------------------------------
const auditorConfig: IaRoleConfig = {
  agentName: "CertiAI · Agente auditor",
  description:
    "Detecta patrones inusuales, expedientes con riesgo y eventos relevantes en la bitácora. Sus alertas siempre quedan trazadas.",
  greeting: (ctx) =>
    `Hola ${effectiveUserName(ctx)}, soy tu agente auditor CertiAI. Trabajo solo con la bitácora y los datos visibles del sistema.`,
  quickChips: [
    "¿Hay inconsistencias?",
    "Certificados por vencer",
    "Resumen de bitácora",
    "Expedientes en riesgo",
  ],
  rules: [
    {
      id: "inconsist",
      keywords: ["inconsist", "anomalía", "anomalia", "patrón", "patron"],
      build: (ctx) => {
        const exps = Object.values(ctx.expedientes);
        const raras = exps.filter(
          (e) => e.evaluacionPractica.aplicada && e.evaluacionPractica.criteriosTotales > 0 && pct(e.evaluacionPractica.criteriosCompetente, e.evaluacionPractica.criteriosTotales) < 50 && e.dictamen === "competente",
        );
        return raras.length === 0
          ? "No detecto inconsistencias entre práctica y dictamen en la vista actual."
          : `Detecto ${raras.length} expediente(s) con práctica por debajo del 50% pero dictamen *competente*: ${raras.map((e) => e.id.toUpperCase()).join(", ")}. Recomiendo revisar fundamento.`;
      },
    },
    {
      id: "vigencia",
      keywords: ["vencer", "vigencia", "recertific"],
      build: (ctx) => {
        const cert = Object.values(ctx.expedientes).filter((e) => e.certificado.emitido);
        return `Hay **${cert.length} certificados vigentes**. La política de vigencia es de 3 años — la siguiente cohorte a recertificar son quienes emitieron antes de hace 30 meses.`;
      },
    },
    {
      id: "bitacora",
      keywords: ["bitácora", "bitacora", "evento", "log"],
      build: (ctx) => {
        const total = ctx.auditLog.length;
        const tipos: Record<string, number> = {};
        for (const e of ctx.auditLog) tipos[e.entidad] = (tipos[e.entidad] ?? 0) + 1;
        return `La bitácora tiene **${total} eventos**. Por entidad:\n${Object.entries(tipos).map(([k, v]) => `· ${k}: ${v}`).join("\n") || "· (vacía)"}`;
      },
    },
    {
      id: "riesgo",
      keywords: ["riesgo", "alerta", "atras", "expediente"],
      build: (ctx) => {
        const exps = Object.values(ctx.expedientes);
        const teorReprobado = exps.filter((e) => e.examenTeorico.aplicado && !e.examenTeorico.aprobado).length;
        const sinPracticaPostTeor = exps.filter((e) => e.examenTeorico.aprobado && !e.evaluacionPractica.aplicada).length;
        return `Señales de riesgo en la vista:\n· ${teorReprobado} expediente(s) con teórico no aprobado.\n· ${sinPracticaPostTeor} con teórico aprobado pero práctica pendiente (riesgo de SLA).`;
      },
    },
  ],
  fallback: () =>
    "Como agente auditor puedo señalarte: inconsistencias práctica/dictamen, vigencia de certificados, resumen de bitácora o expedientes en riesgo.",
};

export const iaConfigByRole: Record<Role, IaRoleConfig> = {
  admin: adminConfig,
  alumno: alumnoConfig,
  instructor: instructorConfig,
  evaluador: evaluadorConfig,
  dictaminador: dictaminadorConfig,
  auditor: auditorConfig,
};

export function buildIaReply(role: Role, userText: string, ctx: IaContext): string {
  const cfg = iaConfigByRole[role];
  const lower = userText.toLowerCase().trim();
  if (!lower) return cfg.fallback(ctx, userText);
  for (const rule of cfg.rules) {
    if (rule.keywords.some((kw) => lower.includes(kw.toLowerCase()))) {
      try {
        return rule.build(ctx);
      } catch {
        return cfg.fallback(ctx, userText);
      }
    }
  }
  return cfg.fallback(ctx, userText);
}
